import {
  SocialAccountType,
  useInfluencerProfileAudienceQuery,
  useInfluencerProfileEstimatedAudienceQuery
} from '@anytag/graphql';
import { ChartsContainer } from '@anytag/shared-atoms';
import { HAS_NO_ESTIMATED_TEXT } from '@anytag/shared-constants';
import {
  AgeBreakdownChart,
  AreaBreakdownChart,
  AreaBreakdownData,
  GenderBreakdownChart,
  GenderBreakdownData
} from '@anytag/shared-molecules';
import { InfluencerSelectedAccountInfo } from '../../../types';
import { useInfluencerProfileOpacityContent } from '../../../hooks';
import { InfluencerProfileAudienceWrapper } from '../InfluencerProfileAudienceWrapper';

export interface InfluencerProfileMixedAudienceProps {
  influencerId: number;
  socialAccount: InfluencerSelectedAccountInfo;
}

// This is mixed audience data from InfluencerProfileAudience and InfluencerProfileEstimatedAudience
export const InfluencerProfileMixedAudience = ({
  influencerId,
  socialAccount
}: InfluencerProfileMixedAudienceProps) => {
  const { opacityContent, hasEstimatedDemographics } = useInfluencerProfileOpacityContent({
    influencerId,
    socialAccount
  });

  const { data, loading } = useInfluencerProfileAudienceQuery({
    variables: {
      pk: influencerId,
      socialAccountId: socialAccount.id,
      socialMedia: socialAccount.type as SocialAccountType
    }
  });
  const { data: estimatedAudience, loading: estimatedAudienceLoading } = useInfluencerProfileEstimatedAudienceQuery({
    variables: {
      socialAccountId: socialAccount.id,
      socialAccountType: socialAccount.type as SocialAccountType
    }
  });

  const audienceData = data?.influencerProfileAudience;
  const estimatedAudienceData = estimatedAudience?.influencerProfileEstimatedAudience;
  const chartOpacityContent = hasEstimatedDemographics ? opacityContent : { wrapperText: HAS_NO_ESTIMATED_TEXT };

  const formattedAudienceData = {
    genderData: {
      maleRate: audienceData?.maleRate,
      femaleRate: audienceData?.femaleRate
    } satisfies GenderBreakdownData,
    ageData: audienceData?.ageGenderRates,
    areaData: {
      followerRegionRate: estimatedAudienceData?.location?.regions?.map((region) => ({
        name: region.regionName,
        rate: region.rate
      })),
      followerCountryRate: estimatedAudienceData?.location?.countries?.reduce<Array<{ id: string; rate: number }>>(
        (acc, country) => (country ? [...acc, { id: country.countryId, rate: country.rate }] : acc),
        []
      )
    } satisfies AreaBreakdownData
  };

  return (
    <InfluencerProfileAudienceWrapper
      data={formattedAudienceData}
      loading={loading || estimatedAudienceLoading}
      hasEstimatedDemographics={hasEstimatedDemographics}
    >
      {({ data: { genderData, ageData, areaData } }) => (
        <ChartsContainer
          css={{ height: '385px' }}
          elements={[
            <GenderBreakdownChart {...genderData} {...chartOpacityContent} />,
            <AgeBreakdownChart {...ageData} {...chartOpacityContent} />,
            <AreaBreakdownChart
              {...areaData}
              wrapperText={HAS_NO_ESTIMATED_TEXT}
              hasEstimatedDemographics // Location data always use estimated api
              hasCountriesRates
            />
          ]}
        />
      )}
    </InfluencerProfileAudienceWrapper>
  );
};
